import {useCallback, useEffect, useState} from 'react'

const STORAGE_KEY_OPEN = 'alpinist_open'
const STORAGE_KEY_CONVERSATION = 'alpinist_conversation'
const STORAGE_KEY_CONVERSATION_FILTER = 'alpinist_conversation_filter'
const STORAGE_KEY_HISTORY_OPEN = 'alpinist_history_open'
const EVENT_OPEN = 'alpinist:open'

type UseAlpinistState = {
  /** Whether Alpinist is open */
  isOpen: boolean
  /** Toggle Alpinist open/closed */
  toggle: () => void
  conversationId: string | null
  onConversationChange: (id: string | null) => void
}

/**
 * Hook to manage Alpinist panel state with sessionStorage persistence.
 * Stores both open/closed state and active conversation ID.
 */
export const useAlpinistUrlSync = (): UseAlpinistState => {
  const [isOpen, setIsOpen] = useState(() => {
    return sessionStorage.getItem(STORAGE_KEY_OPEN) === 'true'
  })

  const [conversationId, setConversationId] = useState<string | null>(() => {
    return sessionStorage.getItem(STORAGE_KEY_CONVERSATION)
  })

  useEffect(() => {
    const handleOpen = (): void => {
      sessionStorage.setItem(STORAGE_KEY_OPEN, 'true')
      setIsOpen(true)
      // openAlpinistPanel(conversationId) stores the conversation to adopt
      // before dispatching the event; pick it up so an already-mounted panel
      // switches to it.
      setConversationId(sessionStorage.getItem(STORAGE_KEY_CONVERSATION))
    }

    window.addEventListener(EVENT_OPEN, handleOpen)
    return () => {
      window.removeEventListener(EVENT_OPEN, handleOpen)
    }
  }, [])

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev
      if (next) {
        sessionStorage.setItem(STORAGE_KEY_OPEN, 'true')
      } else {
        sessionStorage.removeItem(STORAGE_KEY_OPEN)
        sessionStorage.removeItem(STORAGE_KEY_CONVERSATION)
        setConversationId(null)
      }
      return next
    })
  }, [])

  const onConversationChange = useCallback((id: string | null) => {
    setConversationId(id)
    if (id) {
      sessionStorage.setItem(STORAGE_KEY_CONVERSATION, id)
    } else {
      sessionStorage.removeItem(STORAGE_KEY_CONVERSATION)
    }
  }, [])

  return {
    isOpen,
    toggle,
    conversationId,
    onConversationChange,
  }
}

export const openAlpinistPanel = (conversationId?: string): void => {
  if (typeof window === 'undefined') {
    return
  }
  sessionStorage.setItem(STORAGE_KEY_OPEN, 'true')
  if (conversationId) {
    sessionStorage.setItem(STORAGE_KEY_CONVERSATION, conversationId)
  }
  window.dispatchEvent(new Event(EVENT_OPEN))
}

// Export storage keys for use in GlobalUsageTracker
export const ALPINIST_STORAGE_KEYS = {
  open: STORAGE_KEY_OPEN,
  conversation: STORAGE_KEY_CONVERSATION,
  filter: STORAGE_KEY_CONVERSATION_FILTER,
  historyOpen: STORAGE_KEY_HISTORY_OPEN,
} as const
